import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { cn } from '../../utils/cn';
import { supabase } from '../../lib/supabase';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [validToken, setValidToken] = useState(false);

  useEffect(() => {
    // Check if we have a valid recovery token
    const checkSession = async () => {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (session) {
        setValidToken(true);
      } else {
        // Invalid or expired token
        setErrors({ password: 'Invalid or expired reset link. Please request a new one.' });
      }
    };
    checkSession();
  }, []);

  const calculatePasswordStrength = (password) => {
    if (!password) return '';
    if (password?.length < 6) return 'weak';
    if (password?.length < 10) return 'medium';
    if (password?.length >= 10 && /[A-Z]/?.test(password) && /[0-9]/?.test(password)) return 'strong';
    return 'medium';
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (field === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }

    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validToken) {
      setErrors({ password: 'Invalid or expired reset link. Please request a new one.' });
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const { error } = await supabase?.auth?.updateUser({
        password: formData?.password
      });

      if (error) {
        setErrors({ password: error?.message || 'Failed to reset password. Please try again.' });
        setIsLoading(false);
        return;
      }

      setResetSuccess(true);
      setIsLoading(false);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      console.error('Password reset error:', err);
      setErrors({ password: 'An unexpected error occurred. Please try again.' });
      setIsLoading(false);
    }
  };

  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 'weak':
        return 'bg-red-500';
      case 'medium':
        return 'bg-yellow-500';
      case 'strong':
        return 'bg-green-500';
      default:
        return 'bg-gray-300';
    }
  };

  if (resetSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <Icon name="CheckCircle" size={32} className="text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">Password Reset Successful</h2>
              <p className="text-muted-foreground mb-6">
                Your password has been successfully reset. You can now sign in with your new password.
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                Redirecting to login page...
              </p>
              <Button
                onClick={() => navigate('/login')}
                fullWidth
                size="lg"
              >
                Go to Login
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-2xl mb-4 overflow-hidden">
            <img
              src="/assets/images/Photo_Feb_21_2026_12_00_04_AM_1_-1771799585955.png"
              alt="DraftIQ Logo"
              className="w-full h-full object-contain p-2"
            />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Reset Your Password</h1>
          <p className="text-muted-foreground">Enter your new password below</p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Password Field with Strength Indicator */}
            <div>
              <Input
                type="password"
                label="New Password"
                placeholder="Enter your new password"
                value={formData?.password}
                onChange={(e) => handleInputChange('password', e?.target?.value)}
                error={errors?.password}
                required
                disabled={isLoading || !validToken}
              />
              {formData?.password && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-1">
                    <div className={cn("h-1 flex-1 rounded", passwordStrength ? getPasswordStrengthColor() : 'bg-gray-300')} />
                    <div className={cn("h-1 flex-1 rounded", passwordStrength === 'medium' || passwordStrength === 'strong' ? getPasswordStrengthColor() : 'bg-gray-300')} />
                    <div className={cn("h-1 flex-1 rounded", passwordStrength === 'strong' ? getPasswordStrengthColor() : 'bg-gray-300')} />
                  </div>
                  <p className="text-xs text-muted-foreground capitalize">
                    Password strength: {passwordStrength || 'weak'}
                  </p>
                </div>
              )}
            </div>

            {/* Confirm Password Field */}
            <Input
              type="password"
              label="Confirm New Password"
              placeholder="Re-enter your new password"
              value={formData?.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
              error={errors?.confirmPassword}
              required
              disabled={isLoading || !validToken}
            />

            <Button
              type="submit"
              fullWidth
              size="lg"
              loading={isLoading}
              disabled={isLoading || !validToken}
            >
              {isLoading ? 'Resetting...' : 'Reset Password'}
            </Button>

            <div className="text-center mt-4">
              <button
                type="button"
                onClick={() => navigate('/login')}
                disabled={isLoading}
                className="text-sm text-primary hover:text-primary/80 underline-offset-4 hover:underline transition-colors"
              >
                Back to Login
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;